import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useAuth } from '../lib/auth-context.js';
import { useOrg } from '../lib/org-context.js';
import { apiFetch } from '../lib/api.js';
import { useToast } from '../components/Toast.js';
import { PROGRAM_IMAGE_OPTIONS, getProgramImage } from '../lib/program-images.js';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Program {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  priceAmount: string | null;
  pricePeriod: string | null;
  priceNote: string | null;
  sortOrder: number;
}

/* ------------------------------------------------------------------ */
/*  ProgramsPage                                                       */
/* ------------------------------------------------------------------ */

export function ProgramsPage() {
  const { orgId: authOrgId } = useAuth();
  const { viewingOrgId, userRole } = useOrg();
  const { toast } = useToast();

  const orgId = viewingOrgId ?? authOrgId;

  const [programs, setPrograms] = useState<Program[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const fetchPrograms = useCallback(async () => {
    if (!orgId) return;
    try {
      const data = await apiFetch<{ programs: Program[] }>(`/orgs/${orgId}/programs`);
      setPrograms(data.programs);
    } catch {
      toast('error', 'Failed to load programs');
    } finally {
      setIsLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const handleDelete = async (id: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/programs/${id}`, { method: 'DELETE' });
      toast('success', 'Program deleted');
      setConfirmDelete(null);
      fetchPrograms();
    } catch {
      toast('error', 'Failed to delete program');
    }
  };

  const handleReorder = async (index: number, direction: 'up' | 'down') => {
    const newPrograms = [...programs];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newPrograms.length) return;

    // Swap
    [newPrograms[index], newPrograms[swapIdx]] = [newPrograms[swapIdx]!, newPrograms[index]!];
    setPrograms(newPrograms);

    try {
      await apiFetch(`/orgs/${orgId}/programs/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ program_ids: newPrograms.map(p => p.id) }),
      });
    } catch {
      toast('error', 'Failed to reorder');
      fetchPrograms();
    }
  };

  const openEdit = (program: Program) => {
    setEditingProgram(program);
    setShowModal(true);
  };

  const openAdd = () => {
    setEditingProgram(null);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingProgram(null);
    fetchPrograms();
  };

  // Only admins / owners can manage programs
  if (userRole === 'staff') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-sm">You do not have permission to manage programs.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Programs &amp; Services</h2>
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-48 mb-2" />
            <div className="h-3 bg-gray-100 rounded w-32" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Programs &amp; Services</h2>
          <p className="text-sm text-gray-500">Manage your programs and class offerings</p>
        </div>
        <button
          onClick={openAdd}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Program
        </button>
      </div>

      {/* Programs list */}
      {programs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 mb-3">No programs yet</p>
          <button
            onClick={openAdd}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Add your first program
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {programs.map((program, idx) => (
            <div
              key={program.id}
              className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4"
            >
              {/* Reorder arrows */}
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => handleReorder(idx, 'up')}
                  disabled={idx === 0}
                  className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move up"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => handleReorder(idx, 'down')}
                  disabled={idx === programs.length - 1}
                  className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Move down"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Image thumbnail */}
              {program.imageUrl ? (
                <img
                  src={program.imageUrl}
                  alt={program.name}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-gray-900">{program.name}</h3>
                  {program.priceAmount && (
                    <span className="text-xs font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                      ${program.priceAmount}{program.pricePeriod === 'monthly' ? '/mo' : program.pricePeriod === 'weekly' ? '/wk' : program.pricePeriod === 'per_class' ? '/class' : program.pricePeriod === 'drop_in' ? ' drop-in' : ''}
                    </span>
                  )}
                </div>
                {program.description && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{program.description}</p>
                )}
                {program.priceNote && (
                  <p className="text-[10px] text-gray-400 mt-0.5">{program.priceNote}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => openEdit(program)}
                  className="text-xs text-gray-500 hover:text-primary-600 px-2 py-1 rounded hover:bg-gray-50 transition-colors"
                >
                  Edit
                </button>
                {confirmDelete === program.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(program.id)}
                      className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 font-medium transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(program.id)}
                    className="text-xs text-gray-400 hover:text-red-600 px-2 py-1 rounded hover:bg-gray-50 transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <ProgramModal
          orgId={orgId!}
          program={editingProgram}
          onClose={() => { setShowModal(false); setEditingProgram(null); }}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Program Modal                                                      */
/* ------------------------------------------------------------------ */

function ProgramModal({
  orgId,
  program,
  onClose,
  onSuccess,
}: {
  orgId: string;
  program: Program | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(program?.name ?? '');
  const [description, setDescription] = useState(program?.description ?? '');
  const [imageUrl, setImageUrl] = useState(program?.imageUrl ?? getProgramImage(program?.name ?? ''));
  const [priceAmount, setPriceAmount] = useState(program?.priceAmount ?? '');
  const [pricePeriod, setPricePeriod] = useState(program?.pricePeriod ?? 'monthly');
  const [priceNote, setPriceNote] = useState(program?.priceNote ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const isEdit = !!program;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        await apiFetch(`/orgs/${orgId}/programs/${program.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            image_url: imageUrl.trim() || null,
            price_amount: priceAmount.trim() || null,
            price_period: priceAmount.trim() ? pricePeriod : null,
            price_note: priceNote.trim() || null,
          }),
        });
        toast('success', 'Program updated');
      } else {
        await apiFetch(`/orgs/${orgId}/programs`, {
          method: 'POST',
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            image_url: imageUrl.trim() || null,
            price_amount: priceAmount.trim() || null,
            price_period: priceAmount.trim() ? pricePeriod : null,
            price_note: priceNote.trim() || null,
          }),
        });
        toast('success', 'Program created');
      }
      onSuccess();
    } catch {
      toast('error', `Failed to ${isEdit ? 'update' : 'create'} program`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Program' : 'Add Program'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Program Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Kids Karate"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of this program..."
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Image */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Image</label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {PROGRAM_IMAGE_OPTIONS.map(opt => (
                <button
                  key={opt.url}
                  type="button"
                  onClick={() => setImageUrl(opt.url)}
                  className={`relative rounded-lg overflow-hidden border-2 transition-all aspect-[4/3] ${
                    imageUrl === opt.url ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={opt.url}
                    alt={opt.label}
                    className="w-full h-full object-cover"
                    onError={e => { (e.target as HTMLImageElement).src = ''; }}
                  />
                  {imageUrl === opt.url && (
                    <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700">Use custom URL instead</summary>
              <input
                type="url"
                value={imageUrl.startsWith('/images/') ? '' : imageUrl}
                onChange={e => setImageUrl(e.target.value)}
                placeholder="https://example.com/your-image.jpg"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent mt-2"
              />
            </details>
          </div>

          {/* Pricing */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Pricing (optional)</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="text"
                  value={priceAmount}
                  onChange={e => setPriceAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder="0.00"
                  className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <select
                value={pricePeriod}
                onChange={e => setPricePeriod(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              >
                <option value="monthly">/month</option>
                <option value="weekly">/week</option>
                <option value="per_class">/class</option>
                <option value="drop_in">drop-in</option>
                <option value="custom">custom</option>
              </select>
            </div>
            <input
              type="text"
              value={priceNote}
              onChange={e => setPriceNote(e.target.value)}
              placeholder="e.g., First month free, Family discount available"
              maxLength={200}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent mt-2"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Program'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
